import React, {useEffect, useState, useCallback} from "react";
import {collection, onSnapshot, orderBy, query} from "firebase/firestore";

import AddUser from "../components/AddUser";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Details from "../components/Details";
import EditIcon from "@mui/icons-material/Edit";
import Modal from "./Modal";
import {SetModal} from "../features/modalSlice";
import Table from "../ui/Table/Table";
import VisibilityIcon from "@mui/icons-material/Visibility";
import db from "../firebase";
import {selectType} from "../features/modalSlice";
import styled from "styled-components";
import {useDispatch} from "react-redux";
import {useSelector} from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Users = () => {
  const modalType = useSelector(selectType);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState([]);
  const [modal, setModal] = useState("");
  const [search, setSearch] = useState("");
  const [details, setDetails] = useState("");
  const [editForm, setEdit] = useState("");
  const [formId, setFormId] = useState("");
  const closeEditModal = () => {setEdit("");setFormId("");};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchError, setSearchError] = useState("");
  const dispatch = useDispatch();

  // Validation for search input
  const validateSearch = (value) => {
    if (value.length > 50) {
      setSearchError("Search term cannot exceed 50 characters");
      return false;
    }
    if (value.trim() !== value) {
      setSearchError("Search term cannot start or end with spaces");
      return false;
    }
    const specialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
    if (specialChars.test(value)) {
      setSearchError("Search term cannot contain special characters");
      return false;
    }
    setSearchError("");
    return true;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (validateSearch(value)) {
      setSearch(value);
    } else {
      setSearch(value); // Still update the value but show error
    }
  };

  // Memoized filter function for performance
  const filterUsers = useCallback((searchTerm, usersList) => {
    if (!searchTerm.trim()) return usersList;
    
    return usersList.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const location = item.SiteLocation?.toLowerCase() || "";
      const phone = item.phoneNumber?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      
      return name.includes(searchLower) || 
             location.includes(searchLower) || 
             phone.includes(searchLower);
    });
  }, []);

  useEffect(() => {
    try {
      const filteredData = filterUsers(search, users);
      setData(filteredData);
    } catch (err) {
      console.error("Filter error:", err);
      toast.error("Error filtering users");
    }
  }, [search, users, filterUsers]);

  useEffect(() => {
    let unsubscribe;
    
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const userRef = query(
          collection(db, "LoginPhones"),
          orderBy("timestamp", "desc"),
        );
        
        unsubscribe = onSnapshot(userRef, 
          (querySnapshot) => {
            const usersData = querySnapshot.docs.map((doc) => ({
              id: doc.id, 
              ...doc.data()
            }));
            
            // Validate each user object
            const validatedUsers = usersData.filter(user => {
              if (!user.id) {
                console.warn("User missing ID:", user);
                return false;
              }
              return true;
            });
            
            setUsers(validatedUsers);
            setData(validatedUsers);
            setLoading(false);
          },
          (error) => {
            console.error("Snapshot error:", error);
            setError("Failed to fetch users. Please check your connection.");
            setLoading(false);
            toast.error("Failed to fetch users");
          }
        );
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to initialize data fetch");
        setLoading(false);
        toast.error("Failed to initialize data fetch");
      }
    };

    fetchUsers();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    const handlerESC = (event) => {
      const code = event.code;
      if (code === "Escape") {
        setDetails("");
        setModal("");
        setEdit("");
        setFormId("");
        setSearchError("");
      }
    };
    document.addEventListener("keydown", handlerESC);

    return () => {
      document.removeEventListener("keydown", handlerESC);
    };
  }, []);

  const handlerDetails = useCallback((id) => {
    // Validate id
    if (!id) {
      toast.error("Invalid user ID");
      return;
    }

    // Check if user exists
    const userExists = users.some(user => user.id === id);
    if (!userExists) {
      toast.error("User not found");
      return;
    }

    switch (modal) {
      case "LoginPhones":
        setDetails("");
        setModal("");
        break;
      case "":
        setDetails(id);
        setModal("LoginPhones");
        break;
      default:
        setModal("");
    }
  }, [modal, users]);

  const handlerDelete = useCallback(async (id) => {
    // Validate id
    if (!id) {
      toast.error("Invalid user ID for deletion");
      return;
    }

    // Check if user exists
    const userExists = users.some(user => user.id === id);
    if (!userExists) {
      toast.error("User not found for deletion");
      return;
    }

    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    dispatch(
      SetModal({
        type: "Supervisor",
        dbName: "LoginPhones",
        id: id,
      }),
    );
  }, [dispatch, users]);

  const handleEdit = useCallback((formId) => {
    // Validate formId
    if (!formId) {
      toast.error("Invalid user ID for editing");
      return;
    }

    // Check if user exists
    const userExists = users.some(user => user.id === formId);
    if (!userExists) {
      toast.error("User not found for editing");
      return;
    }

    switch (editForm) {
      case "adduser":
        setEdit("");
        setFormId("");
        break;
      case "":
        setFormId(formId);
        setEdit("adduser");
        break;
      default:
        setEdit("");
        setFormId("");
    }
  }, [editForm, users]);

  const columns = [
    {
      id: "profileImg",
      label: "Image",
      Cell: (profileImg) => (
        <BgImg>
          {profileImg ? (
            <img 
              src={profileImg} 
              alt="Profile" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png"; // Fallback image
              }}
            />
          ) : (
            <div className="default-avatar">No Image</div>
          )}
        </BgImg>
      ),
    },
    {id: "name", label: "Name"},
    {id: "SiteLocation", label: "Location"},
    {id: "phoneNumber", label: "Phone No."},
    {
      id: "id",
      label: "Action",
      Cell: (id) => (
        <ButtonWrapper>
          <button 
            onClick={() => handlerDetails(id)}
            aria-label="View details"
            title="View details"
          >
            <VisibilityIcon />
          </button>
          <button 
            onClick={() => handleEdit(id)}
            aria-label="Edit user"
            title="Edit user"
          >
            <EditIcon />
          </button>
          <button
            style={{color: "#F44336"}}
            onClick={() => handlerDelete(id)}
            aria-label="Delete user"
            title="Delete user"
          >
            <DeleteOutlinedIcon />
          </button>
        </ButtonWrapper>
      ),
    },
  ];

  if (loading) {
    return (
      <SitePage>
        <LoadingSpinner>Loading users...</LoadingSpinner>
      </SitePage>
    );
  }

  if (error) {
    return (
      <SitePage>
        <ErrorMessage>
          <h3>Error</h3>
          <p>{error}</p>
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorMessage>
      </SitePage>
    );
  }

  return (
    <>
      <SitePage>
        <SearchContainer>
          <div>
            <h2>Supervisor Data Table</h2>
            <p>Overview of all Supervisors ({data.length} total)</p>
          </div>
          <div>
            <Fields>
              <label htmlFor="search">Search</label>
              <input
                id="search"
                type="text"
                placeholder="Search by name, location, or phone..."
                value={search}
                onChange={handleSearchChange}
                maxLength={50}
              />
              {searchError && <SearchErrorText>{searchError}</SearchErrorText>}
            </Fields>
          </div>
        </SearchContainer>
        {data.length === 0 ? (
          <NoDataMessage>
            {search ? "No users match your search criteria" : "No users found"}
          </NoDataMessage>
        ) : (
          <Table data={data} columns={columns} />
        )}
      </SitePage>
      {editForm === "adduser" && (
      <AddUser 
        editForm={editForm} 
        handlEdit={closeEditModal} 
        formId={formId} 
      />
    )}
      {modal && details && (
        <Details
          details={details}
          handlerDetails={handlerDetails}
          modal={modal}
        />
      )}
      {modalType && <Modal />}
    </>
  );
};

// Styled components with additional styles
const SitePage = styled.div`
  padding: 20px 10px;
  background-color: #fff;
  box-shadow: 0 0 5px 0 rgb(0 0 0 / 10%);
  overflow: hidden;
  position: relative;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  > div {
    &:last-child {
      display: flex;
      align-items: center;
    }
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  width: 250px;
  position: relative;
  margin-right: 15px;
  
  input {
    width: 100%;
    padding: 10px;
    outline: none;
    border: 1px solid rgba(0, 0, 0, 0.3);
    transition: border-color 0.3s;
    
    &:focus {
      border-color: #1976d2;
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
    }
    
    &::placeholder {
      color: #999;
    }
  }
  
  input,
  select {
    height: 35px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 5px;
  }
  
  label {
    position: absolute;
    background-color: #fff;
    font-size: 13px;
    left: 10px;
    top: -12px;
    padding: 3px;
    color: #666666;
    font-weight: 500;
  }
  
  select,
  option {
    padding-right: 50px;
  }
`;

const SearchErrorText = styled.span`
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
  position: absolute;
  bottom: -20px;
  left: 0;
`;

const BgImg = styled.div`
  margin: 0 auto;
  position: relative;
  width: 50px;
  height: 50px;
  overflow: hidden;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: inline-block;
  }

  .default-avatar {
    font-size: 12px;
    color: #999;
    text-align: center;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    font-size: 20px;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    &:focus-visible {
      box-shadow: 0 0 0 2px #1976d2;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  
  h3 {
    color: #f44336;
    margin-bottom: 10px;
  }
  
  p {
    color: #666;
    margin-bottom: 20px;
  }
`;

const RetryButton = styled.button`
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #1565c0;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

export default Users;